import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { callAPI } from "../../utils/apiUtils";
import { apiUrls } from "../../utils/apiUrls";

// Add these new async thunks
export const createStripeCheckout = createAsyncThunk(
  "checkout/createStripeCheckout",
  async (checkoutData, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.checkout, {}, "POST", checkoutData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createPayPalSession = createAsyncThunk(
  "checkout/createPayPalSession",
  async ({ checkoutId, successUrl, cancelUrl }, { rejectWithValue }) => {
    try {
      const response = await callAPI(
        `${apiUrls.paypalSession}`,
        {},
        "POST",
        { checkout_id: checkoutId, success_url: successUrl, cancel_url: cancelUrl }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Existing placeOrder thunk (keep as is)
export const placeOrder = createAsyncThunk(
  "checkout/placeOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.placeOrder, {}, "POST", orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Existing fetchCheckoutData thunk (keep as is)
export const fetchCheckoutData = createAsyncThunk(
  "checkout/fetchCheckoutData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.checkoutData, {}, "GET");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  formData: {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    emailAddress: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    sameAddress: true,
    note: '',
    shippingFirstName: '',
    shippingLastName: '',
    shippingAddress: '',
    shippingCity: '',
    shippingState: '',
    shippingZipCode: '',
    shippingCountry: 'US',
    paymentMethod: 'paypal',
    shippingMethod: 'standard'
  },
  errors: {},
  countries: [],
  states: {},
  paymentMethods: [],
  subtotal: 0,
  discount: 0,
  shipping: 0,
  tax: 0,
  total: 0,
  itemCount: 0,
  checkoutDataLoading: false,
  orderPlacing: false,
  error: null,
  orderSuccess: false,
  orderResult: null,
  // Add these new states
  stripeCheckoutLoading: false,
  stripeSessionLoading: false,
  paypalSessionLoading: false,
  checkoutId: null,
  paymentUrl: null
};

const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    setFormField: (state, action) => {
      const { name, value } = action.payload;
      state.formData[name] = value;
      // Clear error for this field if it exists
      if (state.errors[name]) {
        delete state.errors[name];
      }
    },
    setPaymentMethod: (state, action) => {
      state.formData.paymentMethod = action.payload;
    },
    setShippingMethod: (state, action) => {
      state.formData.shippingMethod = action.payload;
    },
    updateOrderSummary: (state, action) => {
      const { subtotal, itemCount } = action.payload;
      state.subtotal = subtotal;
      state.itemCount = itemCount;
      // Recalculate total
      state.total = subtotal - state.discount + state.shipping + state.tax;
    },
    clearCheckoutError: (state) => {
      state.error = null;
    },
    resetCheckoutState: (state) => {
      state.orderSuccess = false;
      state.orderResult = null;
      state.error = null;
      state.checkoutId = null;
      state.paymentUrl = null;
    },
    validateBillingAddress: (state, action) => {
      // Simplified validation - you can expand as needed
      const errors = {};
      const data = action.payload;
      
      if (!data.firstName) errors.firstName = "First name is required";
      if (!data.lastName) errors.lastName = "Last name is required";
      if (!data.address) errors.address = "Address is required";
      if (!data.city) errors.city = "City is required";
      if (!data.state) errors.state = "State is required";
      if (!data.zipCode) errors.zipCode = "Zip code is required";
      
      state.errors = errors;
    }
  },
  extraReducers: (builder) => {
    // Existing fetchCheckoutData cases
    builder
      .addCase(fetchCheckoutData.pending, (state) => {
        state.checkoutDataLoading = true;
        state.error = null;
      })
      .addCase(fetchCheckoutData.fulfilled, (state, action) => {
        state.checkoutDataLoading = false;
        if (action.payload?.success) {
          const data = action.payload.data;
          state.countries = data.countries || [];
          state.states = data.states || {};
          state.paymentMethods = data.paymentMethods || [];
        }
      })
      .addCase(fetchCheckoutData.rejected, (state, action) => {
        state.checkoutDataLoading = false;
        state.error = action.payload?.message || "Failed to load checkout data";
      })
      
      // Existing placeOrder cases
      .addCase(placeOrder.pending, (state) => {
        state.orderPlacing = true;
        state.error = null;
      })
      .addCase(placeOrder.fulfilled, (state, action) => {
        state.orderPlacing = false;
        if (action.payload?.success) {
          state.orderSuccess = true;
          state.orderResult = action.payload.data;
        } else {
          state.error = action.payload?.message || "Failed to place order";
        }
      })
      .addCase(placeOrder.rejected, (state, action) => {
        state.orderPlacing = false;
        state.error = action.payload?.message || "Failed to place order";
      })
      
      // New createStripeCheckout cases
      .addCase(createStripeCheckout.pending, (state) => {
        state.stripeCheckoutLoading = true;
        state.error = null;
      })
      .addCase(createStripeCheckout.fulfilled, (state, action) => {
        state.stripeCheckoutLoading = false;
        if (action.payload?.success) {
          state.checkoutId = action.payload.data?.payment_id;
        } else {
          state.error = action.payload?.message || "Failed to create checkout";
        }
      })
      .addCase(createStripeCheckout.rejected, (state, action) => {
        state.stripeCheckoutLoading = false;
        state.error = action.payload?.message || "Failed to create checkout";
      })
      
      // New createPayPalSession cases
      .addCase(createPayPalSession.pending, (state) => {
        state.paypalSessionLoading = true;
        state.error = null;
      })
      .addCase(createPayPalSession.fulfilled, (state, action) => {
        state.paypalSessionLoading = false;
        if (action.payload?.success) {
          state.paymentUrl = action.payload.data?.checkout_url;
        } else {
          state.error = action.payload?.message || "Failed to create PayPal session";
        }
      })
      .addCase(createPayPalSession.rejected, (state, action) => {
        state.paypalSessionLoading = false;
        state.error = action.payload?.message || "Failed to create PayPal session";
      });
  }
});

export const {
  setFormField,
  setPaymentMethod,
  setShippingMethod,
  updateOrderSummary,
  clearCheckoutError,
  resetCheckoutState,
  validateBillingAddress
} = checkoutSlice.actions;

export default checkoutSlice.reducer;